import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Typography from "@mui/material/Typography";
import Common from "../../utils/common";
import ConfigurationHelper from "../../configuration/ClientConfigurationHelper";
import Analytics from "../../libs/analytics";
import "./PhotoProducts.scss";

const lineClampStyle = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
};

const getProductField = (product, field) => {
    if (field === 'price_sale') {
        return product?.data?.sale_price ?? product?.sale_price ?? product?.data?.price_sale ?? product?.price_sale;
    }
    if (field === 'link') {
        return product?.data?.linkUrl ?? product?.link;
    }
    return product?.data?.[field] ?? product?.[field];
};

const PhotoProducts = ({ asset, products = [] }) => {
    const isDesktop = useMediaQuery("(min-width:540px)");
    const PER_PAGE_GRID = isDesktop ? 9 : 8;
    const COLS = isDesktop ? 3 : 2;
    const [showAll, setShowAll] = useState(false);
    const [allProducts, setAllProducts] = useState(products);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        setAllProducts(products);
    }, [products]);

    const fetchAllProducts = async () => {
        if (allProducts.length === 20 && asset?.id) {
            setLoadingMore(true);
            try {
                const query = `{
                    content(filter: { content_id: \"${asset.id}\" }) {
                        items {
                            products {
                                product_id
                                image
                                price
                                sale_price
                                title
                                link
                            }
                        }
                    }
                }`;
                const response = await axios.post(ConfigurationHelper.getContentSearchUrl(), { query }, { withCredentials: true });
                const items = response.data?.data?.content?.items || [];
                const fetchedProducts = items.flatMap(item => item.products || []);
                if (fetchedProducts.length > allProducts.length) {
                    setAllProducts(fetchedProducts);
                }
            } catch (err) {
                console.error("Failed to fetch all productsss", err);
            } finally {
                setLoadingMore(false);
            }
        }
    };

    const handleProdClick = (product) => {
        Analytics.productClick(product, asset.id);
        Common.setCustomCookie(asset.owner);
        window.open(getProductField(product, 'link'), "_blank");
    };

    const toggleShowAll = async () => {
        if (!showAll && allProducts.length === 20 && asset?.id) {
            await fetchAllProducts();
        }
        setShowAll((prev) => !prev);
    };

    const visibleProducts = useMemo(() => {
        if (!allProducts || !allProducts.length) return [];
        const base = showAll ? allProducts : allProducts.slice(0, PER_PAGE_GRID);

        if (!showAll) return base;

        const remainder = base.length % COLS;
        if (remainder === 0) return base;

        const dummies = new Array(COLS - remainder).fill({ dummy: true });
        return [...base, ...dummies];
    }, [showAll, allProducts, COLS, PER_PAGE_GRID]);

    return (
        <>
            <Box className="tvp-product-area">
                <Box
                    display="grid"
                    sx={{
                        gridTemplateColumns: {
                            xs: "repeat(2, 1fr)",
                            sm: "repeat(3, 1fr)",
                        },
                        gap: "1px",
                        backgroundColor: "grey.300",
                    }}
                >
                    {visibleProducts.map((product, idx) => (
                        <Box
                            sx={{ backgroundColor: "common.white", p: 1 }}
                            key={idx}
                            className="tvp-product-item"
                        >
                            <Box className="tvp-photo-product-holder">
                                {!product.dummy ? (
                                    <>
                                        <div
                                            className="embed-responsive embed-responsive-1by1 tvp-photo-product"
                                            onClick={() =>
                                                handleProdClick(product)
                                            }
                                        >
                                            <div
                                                className="embed-responsive-item"
                                                style={{
                                                    backgroundImage: `url(${product?.data?.imageUrl || product?.image})`,
                                                }}
                                            ></div>
                                        </div>
                                        <div className="tvp-photo-product-info">
                                            <div
                                                className="tvp-photo-product-title"
                                                title={product.title}
                                                style={lineClampStyle}
                                            >
                                                {product.title}
                                            </div>
                                            {getProductField(product, 'price') && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "baseline",
                                                        gap: 1,
                                                    }}
                                                >
                                                    {getProductField(product, 'price_sale') && Number(getProductField(product, 'price_sale')) < Number(getProductField(product, 'price')) ? (
                                                        <>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{ fontSize: "12px" }}
                                                            >
                                                                <span className="currency-mini">$</span>
                                                                {Common.formatPrice(getProductField(product, 'price_sale'))}
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    textDecoration: "line-through",
                                                                    fontSize: "12px",
                                                                    color: "rgba(114, 114, 114, 1)",
                                                                }}
                                                            >
                                                                <span className="currency-mini">$</span>
                                                                {Common.formatPrice(getProductField(product, 'price'))}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography
                                                            variant="body1"
                                                            sx={{ fontSize: "12px" }}
                                                        >
                                                            <span className="currency-mini">$</span>
                                                            {Common.formatPrice(getProductField(product, 'price'))}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="embed-responsive embed-responsive-1by1 tvp-photo-product-dummy" />
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>

                {allProducts.length > PER_PAGE_GRID && (
                    <Box my={2} textAlign="center">
                        <Button
                            sx={{
                                color: "#000",
                                fontWeight: 700,
                                fontSize: "12px",
                            }}
                            size="small"
                            onClick={toggleShowAll}
                            startIcon={
                                showAll ? (
                                    <ExpandLessIcon />
                                ) : (
                                    <ExpandMoreIcon />
                                )
                            }
                            disabled={loadingMore}
                        >
                            {loadingMore ? "Loading..." : showAll ? "Show Less" : "Show More"}
                        </Button>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default PhotoProducts;
